/**
 * `aava.ts` — client voor de AAVA Intake Conversation API.
 *
 * Twee endpoints worden gewrapped — alleen wat de chat-widget nodig
 * heeft voor de eerste-bericht-flow + vervolg-berichten. De rest van
 * de API (transcripts, /create/item, /submit/item, /end) is werk voor
 * een latere iteratie.
 *
 *   - `POST {base}/aava-repairs/api/v1/conversation/start` → opent een nieuw gesprek
 *     voor een unit, levert `conversationId` terug.
 *   - `POST {base}/aava-repairs/api/v1/conversation/{id}/message` → stuurt een bericht,
 *     krijgt synchroon de assistent-reply terug.
 *
 * ## Auth
 *
 * `Authorization: Bearer <jwt>` — dezelfde JWT die we van de identity-
 * portal kregen. AAVA gebruikt host-driven authenticatie + Hydra's
 * `[Permission(typeof(RepairsConversationStartPermission))]`-filter; een
 * onvoldoende-geautoriseerde caller krijgt 401 vóór de handler.
 *
 * ## Foutpad-strategie (identiek aan `contracts.ts`)
 *
 * Result-types in plaats van throws — caller (controller) doet
 * één `if (!result.ok)`-tak. Reden-codes:
 *   - `network`     — fetch zelf faalde (offline, timeout, DNS)
 *   - `unauthorized` — 401/403; JWT verlopen of permission-mismatch
 *   - `unavailable`  — 5xx of andere non-2xx; tijdelijk server-issue
 *   - `parse`        — 2xx met onverwachte body-shape
 */

import { stripTrailingSlash } from '../utils/url';

export type AavaFetchError =
  | 'network'
  | 'unauthorized'
  | 'unavailable'
  | 'parse';

export type StartAavaConversationInput = {
  /** Guid van de unit waar het gesprek over gaat. */
  unitId: string;
  /** Optionele openingstekst van de bezoeker (eerste user-turn). */
  text?: string;
  /**
   * Optionele bijlagen als data-URIs (`data:<mime>;base64,<bytes>`).
   * Server-side cap: ~28 MiB voor `/start` (ASP.NET default).
   */
  images?: string[];
};

export type StartAavaConversationResult =
  | { ok: true; conversationId: string }
  | { ok: false; reason: AavaFetchError };

export type SendAavaMessageInput = {
  conversationId: string;
  /**
   * Plain-tekst bericht van de bezoeker. Mag in principe leeg zijn
   * (de server checkt niet) — maar caller MOET dit zelf afdwingen
   * want een lege turn levert geen zinnige assistent-reply op.
   */
  text: string;
  /**
   * Optionele bijlagen als data-URIs (`data:<mime>;base64,<bytes>`).
   * Server-side cap: 10 MiB voor de hele `/message`-request inclusief
   * base64-inflation (~33%) — caller MOET zelf per-file budgetteren.
   */
  images?: string[];
};

export type AavaTranscriptMessage = {
  /** `'user'` = bezoeker/agent-impersonatie, `'aidenn'` = AAVA-assistent. */
  sender: 'user' | 'aidenn' | string;
  /** Reply-tekst. Kan `null` zijn voor zeldzame edge-cases. */
  text: string | null;
  /** ISO-timestamp van wanneer het bericht is geschreven. */
  createdAt: string | null;
  /** Inline data-URIs van bijlagen (zie docs §5.2). */
  images: string[];
};

export type GetAavaConversationResult =
  | { ok: true; messages: AavaTranscriptMessage[] }
  | { ok: false; reason: AavaFetchError | 'not_found' };

/**
 * Eén bericht zoals het op de conversation-resource (`GET /conversation/{id}`)
 * staat — mét `messageId` (de `/messages`-transcript-shape draagt 'm niet).
 * De completion-poller gebruikt deze als vangnet om gemiste assistant-replies
 * alsnog te tonen.
 */
export type AavaPolledMessage = {
  /** Monotone per-conversation-ordinal — dedup-sleutel voor reconcile. */
  messageId: number;
  /** `'user'` = bezoeker, `'aidenn'` = AAVA-assistent. */
  sender: 'user' | 'aidenn' | string;
  text: string | null;
  createdAt: string | null;
  images: string[];
};

/**
 * Terminale classificatie van een conversation, zoals de server hem op de
 * GetConversation-resource meegeeft (`Aidenn.Conversation.Outcome`). `Resolved`
 * = tijdens de chat opgelost (self-fix, geen reparatieverzoek); de overige
 * waarden betekenen dat er nog werk voor de corporatie ligt.
 */
export type AavaConversationOutcome =
  | 'Pending'
  | 'RepairRequestCreated'
  | 'Resolved'
  | 'NeedsFollowUp';

const KNOWN_OUTCOMES: readonly AavaConversationOutcome[] = [
  'Pending',
  'RepairRequestCreated',
  'Resolved',
  'NeedsFollowUp',
];

export type AavaConversationStatus = {
  /** `true` zodra de server `finishedAt` heeft gezet — intake is afgerond. */
  finished: boolean;
  /** ISO-timestamp van afronding, of `null` zolang de conversation loopt. */
  finishedAt: string | null;
  /**
   * Scenario-identifier zoals de server hem op de conversation kent
   * (`externalId`). De repair-flow parkeert hier de `scenarioId` in zodat de
   * client na afronding dezelfde `chat-voltooid/{scenarioId}/{conversationId}`
   * portal-route kan opbouwen. `null` wanneer de server geen `externalId` gaf.
   */
  scenarioId: string | null;
  /**
   * De conversation-`messages` (met `messageId`), zoals de poll ze meekrijgt.
   * Leeg wanneer de server geen messages-array gaf. Voedt het reconcile-
   * vangnet voor gemiste assistant-replies.
   */
  messages: AavaPolledMessage[];
  /**
   * Terminale classificatie van de conversation, of `null` wanneer de server
   * geen (herkenbare) `outcome` gaf. Stuurt de self-fix-afhandeling: `Resolved`
   * → yes/no-vraag i.p.v. de `chat-voltooid`-redirect.
   */
  outcome: AavaConversationOutcome | null;
};

export type GetAavaConversationStatusResult =
  | { ok: true; status: AavaConversationStatus }
  | { ok: false; reason: AavaFetchError | 'not_found' };

export type SendAavaMessageResult =
  | {
      ok: true;
      /** Reply van de AAVA-assistent. `null` mogelijk als upstream niets gaf. */
      text: string | null;
      /** Monotone per-conversation-ordinal voor dedup/ordering. */
      messageId: number | null;
      /** Server-side timestamp (ISO-8601). `null` op zeldzame edge-cases. */
      createdAt: string | null;
    }
  | { ok: false; reason: AavaFetchError };

export type AavaFetchOptions = {
  /**
   * Time-budget in ms. Wanneer niet gezet gebruikt elke call z'n eigen
   * default: infra-reads/`/end` op `DEFAULT_TIMEOUT_MS`, de AI-gebonden
   * POSTs (`/start`, `/message`) op het ruimere `AI_REPLY_TIMEOUT_MS`.
   */
  timeoutMs?: number;
  /** Override voor tests. Default: globale `fetch`. */
  fetchImpl?: typeof fetch;
  /** Externe abort (controller-disconnect, SPA-route-change). */
  signal?: AbortSignal;
};

/** Infra-calls (GET messages/status, POST /end) — snelle round-trips. */
const DEFAULT_TIMEOUT_MS = 15_000;

/**
 * Budget voor calls die synchroon op een AAVA-assistent-reply wachten
 * (`/conversation/start` en `/message`). De AI genereert het antwoord
 * server-side vóór de response terugkomt; onder load duurt dat regelmatig
 * langer dan de 15s infra-timeout, waardoor het bericht werd afgebroken
 * ("gecanceld") en de bezoeker een netwerkfout zag (AB#166139). 30s geeft
 * een trage-maar-werkende reply de ruimte om af te ronden, terwijl een
 * écht vastgelopen request nog steeds bounded blijft.
 */
const AI_REPLY_TIMEOUT_MS = 30_000;

/**
 * Opent een nieuwe AAVA-conversatie voor een unit. Gooit **nooit** —
 * elke fout wordt gemapt op een result-object.
 */
export async function startAavaConversation(
  baseUrl: string,
  jwt: string,
  input: StartAavaConversationInput,
  options: AavaFetchOptions = {},
): Promise<StartAavaConversationResult> {
  const url = `${stripTrailingSlash(baseUrl)}/aava-repairs/api/v1/conversation/start`;
  // Alleen velden meesturen die expliciet gezet zijn — sommige
  // deployments balken op extra `null`-keys, en lege arrays op
  // `images` vinden ze ook niet altijd leuk.
  const payload: Record<string, unknown> = { unitId: input.unitId };
  if (input.text !== undefined) payload.text = input.text;
  if (input.images && input.images.length > 0) payload.images = input.images;
  const body = await postJson<{ conversationId?: string }>(
    url,
    jwt,
    payload,
    { ...options, timeoutMs: options.timeoutMs ?? AI_REPLY_TIMEOUT_MS },
  );
  if (!body.ok) return body;
  const id = body.data.conversationId;
  if (typeof id !== 'string' || id.length === 0) {
    return { ok: false, reason: 'parse' };
  }
  return { ok: true, conversationId: id };
}

/**
 * Stuurt een tekst-bericht naar een lopende AAVA-conversatie en wacht
 * synchroon op de assistent-reply.
 */
export async function sendAavaMessage(
  baseUrl: string,
  jwt: string,
  input: SendAavaMessageInput,
  options: AavaFetchOptions = {},
): Promise<SendAavaMessageResult> {
  const url = `${stripTrailingSlash(baseUrl)}/aava-repairs/api/v1/conversation/${encodeURIComponent(
    input.conversationId,
  )}/message`;
  const payload: Record<string, unknown> = { text: input.text };
  if (input.images && input.images.length > 0) payload.images = input.images;
  const body = await postJson<{
    text?: string | null;
    createdAt?: string | null;
    messageId?: number | null;
  }>(url, jwt, payload, {
    ...options,
    timeoutMs: options.timeoutMs ?? AI_REPLY_TIMEOUT_MS,
  });
  if (!body.ok) return body;
  // Defensief parsen — alle drie de velden mogen ontbreken volgens de
  // OpenAPI-types (nullable). We forwarden wat er is.
  return {
    ok: true,
    text: typeof body.data.text === 'string' ? body.data.text : null,
    messageId:
      typeof body.data.messageId === 'number' &&
      Number.isFinite(body.data.messageId)
        ? body.data.messageId
        : null,
    createdAt:
      typeof body.data.createdAt === 'string' ? body.data.createdAt : null,
  };
}

/**
 * Haalt de transcript van een bestaande AAVA-conversatie op. Gebruikt
 * voor session-restore na een page-refresh: client weet alleen het
 * `conversationId`, server is bron-van-waarheid voor alle messages.
 *
 * 404 (`Conversation not found`) wordt als aparte reden teruggegeven
 * zodat de caller weet dat de conversation niet meer bestaat (server-
 * side opgeruimd) en localStorage moet wissen.
 */
export async function getAavaConversation(
  baseUrl: string,
  jwt: string,
  conversationId: string,
  options: AavaFetchOptions = {},
): Promise<GetAavaConversationResult> {
  const url = `${stripTrailingSlash(baseUrl)}/aava-repairs/api/v1/conversation/${encodeURIComponent(
    conversationId,
  )}/messages`;
  const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const fetchImpl = options.fetchImpl ?? fetch;

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  const onExternalAbort = () => controller.abort();
  options.signal?.addEventListener('abort', onExternalAbort);

  try {
    const response = await fetchImpl(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${jwt}`,
        Accept: 'application/json',
      },
      signal: controller.signal,
    });
    if (!response.ok) {
      if (response.status === 404) {
        return { ok: false, reason: 'not_found' };
      }
      if (response.status === 401 || response.status === 403) {
        return { ok: false, reason: 'unauthorized' };
      }
      if (response.status >= 500) {
        return { ok: false, reason: 'unavailable' };
      }
      return { ok: false, reason: 'unavailable' };
    }
    const body = (await response.json()) as unknown;
    if (!body || typeof body !== 'object') {
      return { ok: false, reason: 'parse' };
    }
    const rawMessages = (body as { messages?: unknown }).messages;
    if (!Array.isArray(rawMessages)) {
      return { ok: false, reason: 'parse' };
    }
    const messages: AavaTranscriptMessage[] = [];
    for (const m of rawMessages) {
      if (!m || typeof m !== 'object') continue;
      const obj = m as Record<string, unknown>;
      messages.push({
        sender: typeof obj.sender === 'string' ? obj.sender : 'aidenn',
        text: typeof obj.text === 'string' ? obj.text : null,
        createdAt: typeof obj.createdAt === 'string' ? obj.createdAt : null,
        images: Array.isArray(obj.images)
          ? (obj.images.filter(
              (s) => typeof s === 'string',
            ) as string[])
          : [],
      });
    }
    return { ok: true, messages };
  } catch (error) {
    if (
      !(error instanceof DOMException && error.name === 'AbortError')
    ) {
      console.warn('[chat] getAavaConversation faalde:', error);
    }
    return { ok: false, reason: 'network' };
  } finally {
    clearTimeout(timer);
    options.signal?.removeEventListener('abort', onExternalAbort);
  }
}

/**
 * Pollt de conversation-status (`GET /conversation/{id}`) en leest het
 * `finishedAt`-veld + de `externalId` (waar de repair-flow de `scenarioId`
 * in parkeert). Gebruikt door de completion-poller: zodra `finished` true is,
 * stuurt de client de bezoeker door naar de portal's `chat-voltooid`-route.
 *
 * Spiegelt bewust de PoC-`pollMessages`-flow uit `_AavaChat.cshtml`: poll
 * de conversation, en zodra hij server-side is afgerond → redirect. Anders
 * dan `getAavaConversation` (die `/messages` leest voor de transcript) leest
 * deze de conversation-resource zelf, want alleen die draagt `finishedAt`.
 *
 * Gooit **nooit** — elke fout wordt op een result-object gemapt. 404 wordt
 * als aparte `not_found`-reden teruggegeven (conversation server-side
 * opgeruimd) zodat de poller kan stoppen i.p.v. eindeloos blijven kloppen.
 */
export async function getAavaConversationStatus(
  baseUrl: string,
  jwt: string,
  conversationId: string,
  options: AavaFetchOptions = {},
): Promise<GetAavaConversationStatusResult> {
  const url = `${stripTrailingSlash(baseUrl)}/aava-repairs/api/v1/conversation/${encodeURIComponent(
    conversationId,
  )}`;
  const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const fetchImpl = options.fetchImpl ?? fetch;

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  const onExternalAbort = () => controller.abort();
  options.signal?.addEventListener('abort', onExternalAbort);

  try {
    const response = await fetchImpl(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${jwt}`,
        Accept: 'application/json',
      },
      signal: controller.signal,
    });
    if (!response.ok) {
      if (response.status === 404) {
        return { ok: false, reason: 'not_found' };
      }
      if (response.status === 401 || response.status === 403) {
        return { ok: false, reason: 'unauthorized' };
      }
      return { ok: false, reason: 'unavailable' };
    }
    const body = (await response.json()) as unknown;
    if (!body || typeof body !== 'object') {
      return { ok: false, reason: 'parse' };
    }
    const obj = body as Record<string, unknown>;
    const finishedAt =
      typeof obj.finishedAt === 'string' && obj.finishedAt.length > 0
        ? obj.finishedAt
        : null;
    const scenarioId =
      typeof obj.externalId === 'string' && obj.externalId.length > 0
        ? obj.externalId
        : null;
    const outcome =
      typeof obj.outcome === 'string' &&
      (KNOWN_OUTCOMES as readonly string[]).includes(obj.outcome)
        ? (obj.outcome as AavaConversationOutcome)
        : null;
    const messages: AavaPolledMessage[] = [];
    if (Array.isArray(obj.messages)) {
      for (const m of obj.messages) {
        if (!m || typeof m !== 'object') continue;
        const mo = m as Record<string, unknown>;
        // Zonder numerieke `messageId` kan reconcile niet dedupliceren —
        // sla zo'n entry over i.p.v. 'm met een rommel-key te tonen.
        if (typeof mo.messageId !== 'number' || !Number.isFinite(mo.messageId)) {
          continue;
        }
        messages.push({
          messageId: mo.messageId,
          sender: typeof mo.sender === 'string' ? mo.sender : 'aidenn',
          text: typeof mo.text === 'string' ? mo.text : null,
          createdAt: typeof mo.createdAt === 'string' ? mo.createdAt : null,
          images: Array.isArray(mo.images)
            ? (mo.images.filter((s) => typeof s === 'string') as string[])
            : [],
        });
      }
    }
    return {
      ok: true,
      status: {
        finished: finishedAt !== null,
        finishedAt,
        scenarioId,
        messages,
        outcome,
      },
    };
  } catch (error) {
    if (!(error instanceof DOMException && error.name === 'AbortError')) {
      console.warn('[chat] getAavaConversationStatus faalde:', error);
    }
    return { ok: false, reason: 'network' };
  } finally {
    clearTimeout(timer);
    options.signal?.removeEventListener('abort', onExternalAbort);
  }
}

/**
 * Sluit een lopende AAVA-conversatie aan client-zijde af. Server zet
 * `Conversation.FinishedAt` in de database; de docs noemen 'm
 * "non-enforcing" — server staat berichten na /end nog steeds toe, dus
 * de client moet zelf afdwingen dat er geen vervolg-/message-calls
 * gaan na /end.
 *
 * **Best-effort**: een falende /end-call blokkeert de UI niet — de
 * bezoeker is al onderweg (state-reset volgt direct). Faalt de POST,
 * dan blijft de conversation server-side "open" — niet kritisch want
 * AAVA heeft geen actieve push-mechanismen (alles is client-initiated).
 * Vergelijk met chat-server's `/leave`-pattern (sendBeacon-first).
 */
export async function endAavaConversation(
  baseUrl: string,
  jwt: string,
  conversationId: string,
  options: AavaFetchOptions = {},
): Promise<void> {
  const url = `${stripTrailingSlash(baseUrl)}/aava-repairs/api/v1/conversation/${encodeURIComponent(
    conversationId,
  )}/end`;
  const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const fetchImpl = options.fetchImpl ?? fetch;

  // `keepalive: true` zorgt dat de call de page-unload overleeft (zelfde
  // semantiek als sendBeacon maar mét custom-headers, zodat we de JWT
  // gewoon in Authorization kunnen zetten i.p.v. in de URL — geen
  // bearer-token-leak in server-side request-logs).
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  const onExternalAbort = () => controller.abort();
  options.signal?.addEventListener('abort', onExternalAbort);
  try {
    await fetchImpl(url, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${jwt}`,
        Accept: 'application/json',
      },
      keepalive: true,
      signal: controller.signal,
    });
  } catch {
    /* best-effort — niet loggen, niet blokkeren */
  } finally {
    clearTimeout(timer);
    options.signal?.removeEventListener('abort', onExternalAbort);
  }
}

// ============================================================================
// Helpers
// ============================================================================

type PostJsonOk<T> = { ok: true; data: T };
type PostJsonErr = { ok: false; reason: AavaFetchError };
type PostJsonResult<T> = PostJsonOk<T> | PostJsonErr;

/**
 * Gedeelde POST + JSON-parse + foutclassificatie. Beide endpoints volgen
 * exact hetzelfde patroon (Bearer-auth, JSON body, JSON response), dus
 * we abstraheren 'm één keer.
 */
async function postJson<T>(
  url: string,
  jwt: string,
  body: unknown,
  options: AavaFetchOptions,
): Promise<PostJsonResult<T>> {
  const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const fetchImpl = options.fetchImpl ?? fetch;

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  const onExternalAbort = () => controller.abort();
  options.signal?.addEventListener('abort', onExternalAbort);

  try {
    const response = await fetchImpl(url, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${jwt}`,
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
      signal: controller.signal,
    });

    if (!response.ok) {
      if (response.status === 401 || response.status === 403) {
        return { ok: false, reason: 'unauthorized' };
      }
      if (response.status >= 500) {
        return { ok: false, reason: 'unavailable' };
      }
      // 4xx-overig (404 op een ongeldig conversation-id, 400 op
      // schema-mismatch, etc.). Behandel als unavailable — caller
      // toont een generieke "service niet beschikbaar"-melding.
      return { ok: false, reason: 'unavailable' };
    }

    const parsed = (await response.json()) as unknown;
    if (!parsed || typeof parsed !== 'object') {
      return { ok: false, reason: 'parse' };
    }
    return { ok: true, data: parsed as T };
  } catch (error) {
    if (
      !(error instanceof DOMException && error.name === 'AbortError')
    ) {
      console.warn('[chat] AAVA-call faalde:', error);
    }
    return { ok: false, reason: 'network' };
  } finally {
    clearTimeout(timer);
    options.signal?.removeEventListener('abort', onExternalAbort);
  }
}

